import { z } from 'zod';
/*
  KIR-3612: Autonomous Execution Mode — shared contract between the endpoint
  agent (which reads a coding agent's permission mode from its hooks and
  settings) and the backend policy that turns it into alerts and enforcement.
*/
/**
 * The two autonomy groups the policy reacts to.
 * - `auto`: the agent decides by itself which actions are safe (Claude Code
 *   auto mode). Policy group "Claude Code auto mode".
 * - `yolo`: the agent skips every permission check (Claude Code
 *   `bypassPermissions` / `--dangerously-skip-permissions`). Policy group
 *   "YOLO mode (any agent)".
 */
export const AutonomyGroup = {
    Auto: 'auto',
    Yolo: 'yolo',
};
export const autonomyGroupSchema = z.enum([
    AutonomyGroup.Auto,
    AutonomyGroup.Yolo,
]);
/**
 * Claude Code `permission_mode` values (hook input) / `permissions.defaultMode`
 * (settings) that run without per-action approval. Every other value —
 * `default`, `plan`, `acceptEdits`, `dontAsk`, and anything unknown — is not
 * autonomous. The Go agent mirrors this table (KIR-3612 Part 2).
 */
const PERMISSION_MODE_GROUPS = {
    auto: AutonomyGroup.Auto,
    bypassPermissions: AutonomyGroup.Yolo,
};
/** Every permission mode that {@link classifyPermissionMode} calls autonomous. */
export const AUTONOMOUS_PERMISSION_MODES = Object.keys(PERMISSION_MODE_GROUPS);
/** Autonomy group of a permission mode, or null when it is not autonomous. */
export function classifyPermissionMode(permissionMode) {
    if (permissionMode == null)
        return null;
    return Object.prototype.hasOwnProperty.call(PERMISSION_MODE_GROUPS, permissionMode)
        ? PERMISSION_MODE_GROUPS[permissionMode]
        : null;
}
/** `fileType` of the agent's permission-mode report on `POST /api/scans`. */
export const AGENT_PERMISSION_MODE_FILE_TYPE = 'agent-permission-mode';
/**
 * Platform ids the agent may report for Claude Code: its agent type
 * (`claude_code`) and its pluginhost registration name (`claude`).
 */
export const CLAUDE_CODE_AGENT_PLATFORMS = new Set([
    'claude_code',
    'claude',
]);
/** `fileType` the agent already uses for IDE settings snapshots. */
export const IDE_SETTINGS_FILE_TYPE = 'ide-settings';
/**
 * Key of the Claude Code `permissions.defaultMode` value in the agent's
 * `ide-settings` snapshot (`ide-state:claude_code:auto-approve`).
 */
export const CLAUDE_DEFAULT_MODE_SETTINGS_KEY = 'claude.permissions.defaultMode';
/**
 * Body of an `agent-permission-mode` scan: one permission-mode transition of a
 * coding-agent session, or a block the agent applied. Unknown keys are
 * tolerated so a newer agent can add fields without breaking older backends.
 */
export const agentPermissionModeReportSchema = z.object({
    platform: z.string().min(1).max(64),
    permissionMode: z.string().min(1).max(64),
    sessionId: z.string().max(256).optional(),
    cwd: z.string().max(4096).optional(),
    repoRoot: z.string().max(4096).optional(),
    gitOriginUrl: z.string().max(2048).optional(),
    // Set when the agent denied the session's actions under a block toggle.
    blocked: z.boolean().optional(),
});
/** Finding types the Autonomous Execution Mode scanner emits for Claude Code. */
export const AutonomousFindingType = {
    ClaudeAutoMode: 'claude_auto_mode',
    ClaudeAutoModeBlocked: 'claude_auto_mode_blocked',
    ClaudeYoloMode: 'claude_yolo_mode',
    ClaudeYoloModeBlocked: 'claude_yolo_mode_blocked',
};
/** Setting keys of the Autonomous Execution Mode policy. */
export const AutonomousExecutionSetting = {
    RaiseSeverityWhileAutonomous: 'raiseSeverityWhileAutonomous',
    ForceEnforcePolicies: 'forceEnforcePolicies',
    ForceEnforcePolicyKeys: 'forceEnforcePolicyKeys',
    DetectYoloMode: 'detectYoloMode',
    YoloSeverity: 'yoloSeverity',
    BlockYoloMode: 'blockYoloMode',
    BlockClaudeAutoMode: 'blockClaudeAutoMode',
};
//# sourceMappingURL=autonomous-execution.js.map